/**
 * Gönderilecekler (v0.1.54): the approved e-mail replies that wait for Sercan's last look. Each
 * card shows whom the mail goes to and under which subject, the thread so far, the customer's
 * newest mail in Turkish and English, the reply in both languages, and one button: Gmail'den
 * gönder. The worker sends it and the card turns to "Gönderildi"; a failure shows its reason
 * and offers the button again. The English text can also be copied for a hand send.
 */

import type { DeliveryItem, ListingDraftItem, OutboxItem } from '@twin/contracts';
import { useState } from 'react';
import { usePanel, useStore } from '../app/context.js';
import { formatAge, formatClock } from '../lib/format.js';
import { Icon } from './icons.js';
import { copyText } from './sheets.js';

function sendLabel(item: OutboxItem): string {
  switch (item.send.status) {
    case 'queued':
      return "Sırada · Gmail'den gönderiliyor";
    case 'sending':
      return 'Gönderiliyor';
    case 'sent':
      return `Gönderildi${item.send.at === null ? '' : ` · ${formatClock(item.send.at)}`}`;
    case 'failed':
      return `Gönderilemedi${item.send.error === null ? '' : ` · ${item.send.error}`}`;
    default:
      return 'Gönderilmeyi bekliyor';
  }
}

function Thread({ item, now }: { item: OutboxItem; now: number }) {
  if (item.thread.length === 0) return null;
  return (
    <section className="outbox-thread" aria-label="Yazışma geçmişi">
      <h3 className="lr-sub">Yazışma</h3>
      <ol className="outbox-thread__list">
        {item.thread.map((message) => (
          <li key={message.id} className={message.direction === 'out' ? 'outbox-msg outbox-msg--out' : 'outbox-msg'}>
            <div className="outbox-msg__head key ink-2">
              {message.direction === 'out' ? 'Biz' : 'Müşteri'}
              {message.at === null ? '' : ` · ${formatAge(now - message.at)} önce`}
            </div>
            {message.textTr ? <p className="outbox-msg__tr">{message.textTr}</p> : null}
            <p lang="en" className="outbox-msg__text">
              {message.text}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function OutboxCard({ item, now }: { item: OutboxItem; now: number }) {
  const store = useStore();
  const [open, setOpen] = useState(item.send.status !== 'sent');
  const busy = item.send.status === 'queued' || item.send.status === 'sending';
  const sent = item.send.status === 'sent';
  return (
    <li className={sent ? 'outbox-card outbox-card--sent' : 'outbox-card'}>
      <button type="button" className="outbox-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="outbox-card__title">
          <span className="outbox-card__subject">{item.subject}</span>
          <span className="outbox-card__meta ink-2">
            {item.to === null ? 'alıcı yok' : item.to} · onaylandı {formatClock(item.approvedAt)}
          </span>
        </span>
        <span className={item.send.status === 'failed' ? 'outbox-card__state danger' : 'outbox-card__state ink-2'}>
          {sendLabel(item)}
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} className="ink-3" />
      </button>
      {open ? (
        <div className="outbox-card__body">
          <Thread item={item} now={now} />
          <section className="outbox-block" aria-label="Müşterinin son postası">
            <h3 className="lr-sub">Müşterinin son postası</h3>
            {item.customer.tr ? <p className="outbox-block__tr">{item.customer.tr}</p> : null}
            <p lang="en" className="outbox-block__en">
              {item.customer.en}
            </p>
          </section>
          <section className="outbox-block outbox-block--reply" aria-label="Gönderilecek cevap">
            <h3 className="lr-sub">Gönderilecek cevap</h3>
            {item.reply.tr ? <p className="outbox-block__tr">{item.reply.tr}</p> : null}
            <p lang="en" className="outbox-block__en outbox-block__en--reply">
              {item.reply.en}
            </p>
          </section>
          <div className="outbox-card__actions">
            {sent ? null : (
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || item.to === null}
                onClick={() => void store.sendReply(item.id)}
              >
                <Icon name="send" size={16} />
                {item.send.status === 'failed' ? 'Yeniden gönder' : "Gmail'den gönder"}
              </button>
            )}
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() =>
                void copyText(item.reply.en).then((ok) =>
                  store.showToast(ok ? 'İngilizce metin kopyalandı' : 'Kopyalanamadı', {
                    icon: ok ? 'check' : 'alert',
                  }),
                )
              }
            >
              <Icon name="copy" size={16} />
              Metni kopyala
            </button>
            {item.to === null ? <span className="danger">Alıcı adresi bulunamadı; elle gönder.</span> : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

function linkLabel(item: DeliveryItem): string {
  if (item.sent) return 'Gönderildi';
  switch (item.link.status) {
    case 'ready':
      return 'Link hazır';
    case 'waiting':
      return 'Link hazırlanıyor';
    case 'failed':
      return 'Link üretilemedi';
    case 'no_files':
      return 'Dosya anahtarı yok';
    default:
      return 'Portal bağlantısı yok';
  }
}

/**
 * Teslimat (v0.1.114): an approved delivery message. Twin made the download link on the Portal and
 * put it in place of [LINK]; Sercan copies the text, sends it on Etsy and presses Gönderdim.
 */
export function DeliveryCard({ item }: { item: DeliveryItem }) {
  const store = useStore();
  const [open, setOpen] = useState(!item.sent);
  const ready = item.link.status === 'ready';
  return (
    <li className={item.sent ? 'outbox-card outbox-card--sent' : 'outbox-card'}>
      <button type="button" className="outbox-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="outbox-card__title">
          <span className="outbox-card__subject">{item.title}</span>
          <span className="outbox-card__meta ink-2">
            Etsy #{item.orderId} · onaylandı {formatClock(item.approvedAt)}
          </span>
        </span>
        <span className={ready || item.sent ? 'outbox-card__state ink-2' : 'outbox-card__state danger'}>
          {linkLabel(item)}
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} className="ink-3" />
      </button>
      {open ? (
        <div className="outbox-card__body">
          <section className="outbox-block outbox-block--reply" aria-label="Gönderilecek teslimat mesajı">
            <h3 className="lr-sub">Gönderilecek mesaj</h3>
            {item.reply.tr ? <p className="outbox-block__tr">{item.reply.tr}</p> : null}
            <p lang="en" className="outbox-block__en outbox-block__en--reply">
              {item.reply.en}
            </p>
            {item.link.note ? <p className="danger">{item.link.note}</p> : null}
          </section>
          <div className="outbox-card__actions">
            <button
              type="button"
              className={ready ? 'btn btn--primary' : 'btn btn--secondary'}
              onClick={() =>
                void copyText(item.reply.en).then((ok) =>
                  store.showToast(
                    ok ? (ready ? 'Linkli metin kopyalandı' : 'Metin kopyalandı ([LINK] duruyor)') : 'Kopyalanamadı',
                    {
                      icon: ok ? 'check' : 'alert',
                      ...(ok
                        ? { link: { label: 'Etsy siparişlerini aç ↗', href: 'https://www.etsy.com/your/orders/sold' } }
                        : {}),
                    },
                  ),
                )
              }
            >
              <Icon name="copy" size={16} />
              {ready ? 'Linkli metni kopyala' : 'Metni kopyala'}
            </button>
            {item.sent ? null : (
              <button type="button" className="btn btn--secondary" onClick={() => void store.markSent(item.id)}>
                <Icon name="check" size={16} />
                Gönderdim
              </button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}

const NEW_LISTING_URL = 'https://www.etsy.com/your/shops/me/listing-editor/create';

/**
 * Yeni Etsy ilanı (v0.1.223): an approved new listing draft. Twin cannot create Etsy listings; Sercan opens a new
 * listing, copies each field into it and presses Yaptım.
 */
export function ListingDraftCard({ item }: { item: ListingDraftItem }) {
  const store = useStore();
  const [open, setOpen] = useState(!item.done);
  const copy = (value: string, what: string) =>
    void copyText(value).then((ok) =>
      store.showToast(ok ? `${what} kopyalandı` : 'Kopyalanamadı', {
        icon: ok ? 'check' : 'alert',
        ...(ok ? { link: { label: "Etsy'de yeni ilan aç ↗", href: NEW_LISTING_URL } } : {}),
      }),
    );
  const fields: { label: string; value: string; what: string }[] = [
    { label: 'Başlık', value: item.titleEn, what: 'Başlık' },
    { label: 'Etiketler', value: item.tags.join(', '), what: 'Etiketler' },
    { label: 'Açıklama', value: item.bodyEn, what: 'Açıklama' },
  ];
  return (
    <li className={item.done ? 'outbox-card outbox-card--sent' : 'outbox-card'}>
      <button type="button" className="outbox-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="outbox-card__title">
          <span className="outbox-card__subject">{item.title}</span>
          <span className="outbox-card__meta ink-2">
            {item.sku} · onaylandı {formatClock(item.approvedAt)}
          </span>
        </span>
        <span className="outbox-card__state ink-2">{item.done ? 'Yapıldı' : "Etsy'de açılmayı bekliyor"}</span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} className="ink-3" />
      </button>
      {open ? (
        <div className="outbox-card__body">
          <p className="outbox-block__tr">
            Twin Etsy'de ilan açamaz. Etsy'de yeni ilan aç, alanları sırayla kopyalayıp yapıştır
            {item.price === null ? '' : `, fiyatı ${item.price} yap`}; bitince Yaptım'a bas.
          </p>
          {fields.map((field) => (
            <section key={field.label} className="outbox-block outbox-block--reply" aria-label={field.label}>
              <h3 className="lr-sub">{field.label}</h3>
              <p lang="en" className="outbox-block__en outbox-block__en--reply">
                {field.value}
              </p>
              <button type="button" className="btn btn--secondary" onClick={() => copy(field.value, field.what)}>
                <Icon name="copy" size={16} />
                {field.label} kopyala
              </button>
            </section>
          ))}
          <div className="outbox-card__actions">
            <a className="btn btn--secondary" href={NEW_LISTING_URL} target="_blank" rel="noreferrer">
              Etsy'de yeni ilan aç ↗
            </a>
            {item.done ? null : (
              <button type="button" className="btn btn--primary" onClick={() => void store.markSent(item.id)}>
                <Icon name="check" size={16} />
                Yaptım
              </button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** The page: waiting first, sent in the last day after them. */
export function OutboxPage() {
  const state = usePanel();
  const items = state.snapshot?.outbox ?? [];
  const deliveries = state.snapshot?.deliveries ?? [];
  const listingDrafts = state.snapshot?.listingDrafts ?? [];
  const waiting = items.filter((item) => item.send.status !== 'sent');
  const done = items.filter((item) => item.send.status === 'sent');
  return (
    <section className="kb-page" aria-label="Gönderilecekler">
      {/* The section page says what this is in one line; the longer help opens here (audit 25 Oct 2026, F18). */}
      <details className="kb-page__help">
        <summary className="kb-page__help-summary">
          <Icon name="chevron-down" size={16} />
          Nasıl çalışır
        </summary>
        <p className="kb-page__lead">
          Onayladığın e-posta cevapları burada son bir kez okunur ve Gmail'den gönderilir. Gönder dediğinde cevap
          müşterinin postasına yanıt olarak gider, konuşma "müşteriyi bekliyor" olur ve Telegram'a "Gönderildi" düşer.
          Teslimat mesajlarında indirme linkini Twin üretir; linkli metni kopyalayıp Etsy'den gönderirsin. Yeni Etsy
          ilanlarını Twin açamaz; başlığı, etiketleri ve açıklamayı Etsy'de yeni ilana sen yapıştırırsın.
        </p>
      </details>
      {deliveries.length > 0 ? (
        <>
          <h2 className="lr-sub">Teslimat mesajları</h2>
          <ul className="outbox-list" aria-label="Teslimat mesajları">
            {deliveries.map((item) => (
              <DeliveryCard key={`${item.id}-${item.link.status}-${item.sent}`} item={item} />
            ))}
          </ul>
        </>
      ) : null}
      {listingDrafts.length > 0 ? (
        <>
          <h2 className="lr-sub">Yeni Etsy ilanları</h2>
          <ul className="outbox-list" aria-label="Yeni Etsy ilanları">
            {listingDrafts.map((item) => (
              <ListingDraftCard key={`${item.id}-${item.done}`} item={item} />
            ))}
          </ul>
        </>
      ) : null}
      {waiting.length === 0 && done.length === 0 && deliveries.length === 0 && listingDrafts.length === 0 ? (
        <p className="kb-page__empty">Gönderilmeyi bekleyen cevap yok.</p>
      ) : null}
      {waiting.length > 0 ? (
        <ul className="outbox-list" aria-label="Gönderilmeyi bekleyenler">
          {waiting.map((item) => (
            <OutboxCard key={`${item.id}-${item.send.status}`} item={item} now={state.now} />
          ))}
        </ul>
      ) : null}
      {done.length > 0 ? (
        <>
          <h2 className="lr-sub">Son 24 saatte gönderilenler</h2>
          <ul className="outbox-list" aria-label="Gönderilenler">
            {done.map((item) => (
              <OutboxCard key={`${item.id}-sent`} item={item} now={state.now} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
